import { computed, observable, action } from 'mobx';

import type { IIcoImport, IcoImportState } from 'exports/types/api';
import { customFetch } from 'utils/fetch';
import Routes from 'routes';
import * as API from 'exports/types/api';

export type IcoWithIcoImport = Pick<
  API.IIco,
  | 'id'
  | 'identifier'
  | 'weight'
  | 'origin_country_code'
  | 'destination_port_name'
  | 'destination_country_code'
> &
  PartialPick<IIcoImport, 'destination_warehouse_id' | 'bin' | 'arrival_date' | 'state'>;

class IcoImport {
  @observable id?: number;
  @observable destinationWarehouseId?: number;
  @observable bin: string;
  @observable arrivalDate: string;
  @observable state: IcoImportState;
  @observable inNetsuite: boolean;
  @observable isInternalContract: boolean;
  @observable isPendingSubmissionToNetsuite: boolean;

  public identifier: string;
  public weight: string;
  public originCountryCode?: string;
  public destinationPortName?: string;
  public destinationCountryCode?: string;

  constructor({
    id,
    identifier,
    destination_warehouse_id,
    bin = '',
    state = 'new',
    arrival_date = new Date().toISOString().split('T')[0],
    weight = '',
    origin_country_code,
    destination_port_name,
    destination_country_code,
    in_netsuite,
    is_internal_contract,
    is_pending_submission_to_netsuite,
  }: IcoWithIcoImport) {
    this.id = id;
    this.identifier = identifier;
    this.destinationWarehouseId = destination_warehouse_id;
    this.bin = bin;
    this.arrivalDate = arrival_date;
    this.state = state;
    this.weight = weight;
    this.originCountryCode = origin_country_code;
    this.destinationPortName = destination_port_name;
    this.destinationCountryCode = destination_country_code;
    this.inNetsuite = in_netsuite;
    this.isInternalContract = is_internal_contract;
    this.isPendingSubmissionToNetsuite = is_pending_submission_to_netsuite;
  }

  @action public setDestinationWarehouse = (id: number) => {
    this.destinationWarehouseId = id;
  };

  @action public setBin = (value: string) => {
    this.bin = value;
  };

  @action public setArrivalDate = (value: string) => {
    this.arrivalDate = value;
  };

  @action public createOrUpdateIcoImport = () => {
    const icoImportPayload = {
      ico_id: this.id,
      destination_warehouse_id: this.destinationWarehouseId,
      arrival_date: this.arrivalDate,
      bin: this.bin,
    };

    customFetch(
      Routes.create_or_update_api_v1_exports_ico_imports_path(),
      { ico_import: icoImportPayload },
      'POST'
    ).then((response) => {
      if (response.success === true) {
        this.state = 'closed';
      }
    });
  };

  @action public sendToNetsuite = () => {
    const icoImportPayload = {
      ico_id: this.id,
      destination_warehouse_id: this.destinationWarehouseId,
      arrival_date: this.arrivalDate,
      bin: this.bin,
    };

    customFetch(
      Routes.send_to_netsuite_import_api_v1_exports_ico_imports_path(),
      { ico_import: icoImportPayload },
      'POST'
    ).then((response) => {
      if (response.success === true) {
        this.state = 'imported';
      } else {
        console.error('Failed to transition IcoImport to imported:', response.message);
      }
    });
  };

  @action public setInNetsuite = (value: boolean) => {
    this.inNetsuite = value;
  };

  @action public fetchInNetsuiteStatus = () => {
    const icoImportPayload = {
      ico_id: this.id,
      destination_warehouse_id: this.destinationWarehouseId,
      arrival_date: this.arrivalDate,
      bin: this.bin,
    };

    customFetch(
      Routes.get_in_netsuite_status_api_v1_exports_ico_imports_path(),
      { ico_import: icoImportPayload },
      'POST'
    ).then((response) => {
      console.log('Response from fetchInNetsuiteStatus:', response); // Agregar log para depuración
      if (response && response.in_netsuite !== undefined) {
        this.setInNetsuite(response.in_netsuite);
      } else {
        console.error('Failed to fetch in_netsuite status:', response.message);
      }
    });
  };

  @action public setIsInternalContract = (value: boolean) => {
    this.isInternalContract = value;
  };

  @action public getIsInternalContract = () => {
    const icoImportPayload = {
      ico_id: this.id,
      destination_warehouse_id: this.destinationWarehouseId,
      arrival_date: this.arrivalDate,
      bin: this.bin,
    };

    customFetch(
      Routes.is_internal_contract_api_v1_exports_ico_imports_path(),
      { ico_import: icoImportPayload },
      'POST'
    ).then((response) => {
      //console.log('Response from is_internal_contract:', response); // Agregar log para depuración
      if (response && response.is_internal_contract !== undefined) {
        this.setIsInternalContract(response.is_internal_contract);
      } else {
        console.error('Failed to getIsInternalContract status:', response);
      }
    });
  };

  @action public setPendingSubmissionToNetsuite = (value: boolean) => {
    this.isPendingSubmissionToNetsuite = value;
  };

  @action public getPendingSubmissionToNetsuite = () => {
    const icoImportPayload = {
      ico_id: this.id,
      destination_warehouse_id: this.destinationWarehouseId,
      arrival_date: this.arrivalDate,
      bin: this.bin,
    };

    customFetch(
      Routes.pending_submission_to_netsuite_api_v1_exports_ico_imports_path(),
      { ico_import: icoImportPayload },
      'POST'
    ).then((response) => {
      // console.log('Response from pending_submission_to_netsuite:', response); // Agregar log para depuración
      if (response && response.is_pending_submission_to_netsuite !== undefined) {
        this.setPendingSubmissionToNetsuite(response.is_pending_submission_to_netsuite);
      } else {
        console.error(
          'Failed to get pending_submission_to_netsuite status:',
          response.message
        );
      }
    });
  };

  @computed get isClosed(): boolean {
    return this.state === 'closed' || this.state === 'imported';
  }

  @computed get isCloseable(): boolean {
    return !!(this.arrivalDate && this.destinationWarehouseId && this.bin);
  }

  @computed get isImported(): boolean {
    return this.state === 'imported';
  }

  @computed get isInNetsuite(): boolean {
    return !!this.inNetsuite;
  }
}

export default IcoImport;
